import { ICondition, IProfessionalQualification } from '@medely/types';
import useGraphQLRequest from './useGraphQLRequest';
import { DEFAULT_REFETCH_INTERVAL, GET_SKILLS_ASSESSMENTS } from '@mp';
import { useMissingConditions } from './useMissingConditions';
import { QueryFunctionContext, useQuery } from '@tanstack/react-query';
import { useCurrentUser } from './useCurrentUser';
import { useCallback } from 'react';

type UseSearchSkillsAssessmentsInput = {
  refetch?: boolean;
  statuses?: string[];
  skills_assessments?: boolean;
};

const useSearchSkillsAssessments = ({
  refetch,
  statuses,
  skills_assessments,
}: UseSearchSkillsAssessmentsInput) => {
  const { currentUser } = useCurrentUser();
  const { request } = useGraphQLRequest();
  const enabled = !!currentUser;

  const fetchSkillsAssessments = async ({
    queryKey: [_, _statuses, _skills_assessments],
  }: QueryFunctionContext<[string, string[] | undefined, boolean | undefined]>) => {
    const result = (await request(GET_SKILLS_ASSESSMENTS, {
      input: {
        search: {
          not_archived: true,
          qualification_types: ['typeform', 'external_skills_assessment'],
          skills_assessments: _skills_assessments,
          statuses: _statuses,
        },
      },
    })) as { professionalQualifications: IProfessionalQualification[] };
    return result.professionalQualifications;
  };

  const {
    data: skillsAssessments,
    refetch: refetchSkillsAssessments,
    ...props
  } = useQuery(['skillsAssessments', statuses, skills_assessments], fetchSkillsAssessments, {
    enabled,
    refetchInterval: refetch ? DEFAULT_REFETCH_INTERVAL : undefined,
  });

  return {
    ...props,
    skillsAssessments: skillsAssessments ?? [],
    refetch: refetchSkillsAssessments,
  };
};

type UseSkillsAssessmentsResult = {
  completedSkillsAssessments: IProfessionalQualification[];
  missingSkillsAssessments: ICondition[];
  isLoading: boolean;
  refetch: () => Promise<unknown>;
};

const useSkillsAssessments = (
  input: UseSearchSkillsAssessmentsInput = {},
): UseSkillsAssessmentsResult => {
  const { conditions, refetch: refetchMissingConditions } = useMissingConditions({
    refetch: input.refetch,
  });
  const {
    isLoading,
    skillsAssessments,
    refetch: refetchSkillsAssessments,
  } = useSearchSkillsAssessments(input);

  const approvedSkillsQualificationIds = skillsAssessments
    ?.filter((skill: IProfessionalQualification) => skill?.status === 'approved')
    ?.map((skill: IProfessionalQualification) => skill?.qualification?.id);

  const pendingStatusSkillsAssessments = skillsAssessments?.filter(
    (sa: IProfessionalQualification) => sa.status === 'pending',
  );

  const refetch = useCallback(
    () => Promise.all([refetchMissingConditions(), refetchSkillsAssessments()]),
    [refetchMissingConditions, refetchSkillsAssessments],
  );

  return {
    completedSkillsAssessments: skillsAssessments,
    missingSkillsAssessments: conditions?.filter((c) => {
      const qualification = c?.qualifications?.[0];
      const isPendingStatus: boolean | undefined = pendingStatusSkillsAssessments?.some(
        (sa: IProfessionalQualification) => sa.qualification?.id === qualification?.id,
      );

      return (
        qualification?.qualification_type === 'typeform' &&
        !approvedSkillsQualificationIds?.includes(qualification?.id) &&
        !isPendingStatus
      );
    }),
    isLoading,
    refetch,
  };
};

export default useSkillsAssessments;
